import React, { useEffect, useState } from "react";
import IWalletForm from "./IWalletForm";
import { Form } from "antd";
import InfoBox from "@appComponents/InfoBox/InfoBox";
import { dropdownSelectRef } from "@appComponents/Dropdown/Dropdown";
import { MAIN_CURRENCY } from "@bundles/consts";
import { catchError, checkAddress } from "@features/utils/functions";
import FundsService from "@services/Funds.service";
import { useCryptoWallets } from "@funds/components/CryptoWallets/useCryptoWallets";
import FormSelect from "@funds/components/FormSelect";
import FormInput from "@funds/components/FormInput";
import NavigationFooter from "../NavigationFooter";
import {
  WithdrawalProcessSteps,
  useWithdrawalProcess,
} from "../../useWithdrawalProcess";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import { updateCryptoWallets } from "@funds/features/action.funds";
import { IPrivateWallets } from "@funds/components/WithdrawAmount/IWithdrawAmount";

import * as style from "./WalletFormStyle.module.scss";
const stableCoinCurrenciesSelectOptions = [
  {
    value: "usdt",
    label: "USDT Ethereum (ERC-20)",
  },
];
export const selectWalletTypeOptions = [
  {
    value: "erc20",
    label: "Ethereum",
  },
];

const WalletForm: React.FunctionComponent<IWalletForm> = (
  props: IWalletForm
) => {
  const { state, setState } = useWithdrawalProcess();
  const [disabledButton, setDisabledButton] = useState(true);

  const { values, errors, dispatch } = useCryptoWallets();

  useEffect(() => {
    if (dropdownSelectRef && dropdownSelectRef.current) {
      dropdownSelectRef.current.disabled = true;
    }
    return () => {
      if (dropdownSelectRef && dropdownSelectRef.current) {
        dropdownSelectRef.current.disabled = false;
      }
    };
  }, []);

  useEffect(() => {
    setDisabledButton(
      values.address === undefined ||
        !!walletAddressWarning(values.address) ||
        !!errors.address
    );
  }, [values.address]);

  const walletAddressWarning = (value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    const { currency } = props;
    const usdWalletType =
      values.usd_wallet_type && currency === MAIN_CURRENCY
        ? values.usd_wallet_type.value
        : undefined;

    return checkAddress(value, currency, usdWalletType);
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isWalletAddressField?: boolean
  ) => {
    const { value, name } = e.target;
    let errors;

    if (isWalletAddressField) {
      errors = walletAddressWarning(value);
    } else {
      errors = walletAddressWarning(values.address);
    }

    dispatch({
      type: "edit",
      payload: {
        value,
        name,
        errors,
      },
    });
  };

  const onCurrencySelectChange = (value: { value: string; label: string }) => {
    dispatch({
      type: "edit",
      payload: {
        value,
        name: "currency",
        errors: walletAddressWarning(values.address),
      },
    });
  };

  const onWalletCurrencySelectChange = (value: {
    value: string;
    label: string;
  }) => {
    dispatch({
      type: "edit",
      payload: { value, name: "bfusd_wallet_currency" },
      relatedData: [
        {
          name: "selectedWalletCurrency",
          value: value.value,
        },
      ],
      errors: walletAddressWarning(values.address),
    });
  };

  const getCurrencyForAPI = () =>
    state.currency === MAIN_CURRENCY && values.bfusd_wallet_currency
      ? values.bfusd_wallet_currency.value
      : state.currency!;

  const saveNewWallet = () => {
    setDisabledButton(true);

    const data = {
      type: "withdraw",
      currency: getCurrencyForAPI(),
      address: values.address!,
      network:
        state.currency === MAIN_CURRENCY && values.usd_wallet_type
          ? values.usd_wallet_type.value
          : undefined,
      name: values.name,
    };

    FundsService.addCryptoWallet(props.investorId, data)
      .then((res) => {
        props.updateCryptoWallets(res.data);
        setDisabledButton(false);
        setState({
          ...state,
          step: WithdrawalProcessSteps.WITHDRAWAL_CRYPTO,
        });
      })
      .catch((err) => {
        const errors = Object.keys(err.response.data.errors).reduce(
          (prev, curr) => ({
            ...prev,
            [curr]: err.response.data.errors[curr][0],
          }),
          {}
        );
        if (Object.keys(errors).length) {
          dispatch({ type: "setErrors", errors: errors });
        } else {
          catchError(err);
        }
        window.scrollTo(0, 0);
        setDisabledButton(false);
      });
  };

  useEffect(() => {
    dispatch({
      type: "edit",
      payload: {
        value: {
          value: props.currency ?? "",
          label: props.currency?.toUpperCase() ?? "",
        },
        name: "currency",
      },
    });
  }, []);

  useEffect(() => {
    if (props.currency === MAIN_CURRENCY) {
      onWalletCurrencySelectChange(stableCoinCurrenciesSelectOptions[0]);
    }
  }, [props.currency]);

  return (
    <>
      <Form className={style.walletForm}>
        {props.currencies && (
          <FormSelect
            selectProps={{
              onChange: (selectedValue) =>
                onCurrencySelectChange({
                  value: selectedValue,
                  label: props.currencies?.find(
                    (currency) => currency.value === selectedValue
                  )?.label!,
                }),
              defaultValue: props.currencies.find(
                (el) => el.value === props.currency
              )?.value,
              options: props.currencies,
              style: { width: "100%" },
            }}
            formItemProps={{
              label: "Currency",
            }}
          />
        )}
        <FormInput
          formItemProps={{
            label: "Wallet name",
            name: "name",
            help: values.name !== undefined ? (errors as any)?.name : "",
          }}
          inputProps={{
            onChange: (e) => onChange(e, false),
            defaultValue: values.name || "",
            name: "name",
            "data-cy": "wallet-name-field",
          }}
        />
        {props.currency === MAIN_CURRENCY && (
          <FormSelect
            selectProps={{
              onChange: () =>
                onWalletCurrencySelectChange({
                  ...stableCoinCurrenciesSelectOptions[0],
                }),
              defaultValue: stableCoinCurrenciesSelectOptions[0],
              options: stableCoinCurrenciesSelectOptions,
              style: { width: "100%" },
              disabled: true,
            }}
            formItemProps={{ label: "Wallet Currency" }}
          />
        )}

        <FormInput
          formItemProps={{
            label: "Wallet address",
            name: "address",
            help: values.address !== undefined ? errors.address : "",
          }}
          inputProps={{
            "data-cy": "wallet-address-field",
            onChange: (e) => onChange(e, true),
            defaultValue: values.address || "",
            name: "address",
            disabled: !Boolean(props.currency),
          }}
        />

        {walletAddressWarning(values.address) && (
          <InfoBox>
            <p className={style.info}>{walletAddressWarning(values.address)}</p>
          </InfoBox>
        )}
      </Form>
      <NavigationFooter
        onCancel={() =>
          setState({ ...state, step: WithdrawalProcessSteps.WITHDRAWAL_CRYPTO })
        }
        disabled={disabledButton}
        onContinue={async () => {
          await saveNewWallet();
        }}
      />
    </>
  );
};

const mapStateToProps = (store: TReducers) => ({
  investorId: store.userInfo.investor_id,
});

const mapDispatchToProps = (dispatch: Function) => ({
  updateCryptoWallets: (data: IPrivateWallets, walletIdToRemove?: number) =>
    dispatch(updateCryptoWallets(data, walletIdToRemove)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WalletForm) as any;
